// ABOUTME: SPEC-82 review L1 — the pre-tap request queue behind the welcome: a screen move replaces the one already held, every other
// ABOUTME: request is kept in order, and delivery waits for an open socket (a request flushed into a closed one is dropped silently).

import { useCallback, useEffect, useMemo, useRef } from 'react';

// A blink reconnect during the welcome is the case: App's backoff is 1/2/4/8/15 s (SPEC-11), so ~30 s covers all five attempts.
export const SOCKET_RETRY_MS = 500;
export const SOCKET_GIVE_UP_MS = 40000;

/**
 * @param {object} p
 * @param {Function} [p.isSocketOpen] whether the agent socket is open right now; absent = always open
 * @returns {{ hold: (slot: (string|null), send: Function) => void, deliver: () => void }}
 *   `hold` queues a request (a non-null `slot` replaces the one already held in it); `deliver` sends everything held, in order,
 *   as soon as the socket is open. Never throws: a queued send that fails is logged and the rest still go.
 */
export default function useHeldRequests({ isSocketOpen }) {
  const queueRef = useRef([]);
  const retryRef = useRef(null);
  const nowTimersRef = useRef([]);
  const openRef = useRef(isSocketOpen);
  openRef.current = isSocketOpen;

  const hold = useCallback((slot, send) => {
    if (slot) queueRef.current = queueRef.current.filter((entry) => entry.slot !== slot);
    queueRef.current.push({ slot, send });
  }, []);

  const deliver = useCallback((waitedMs = 0) => {
    const open = openRef.current ? openRef.current() : true;
    if (!open && waitedMs < SOCKET_GIVE_UP_MS) {
      retryRef.current = setTimeout(() => deliver(waitedMs + SOCKET_RETRY_MS), SOCKET_RETRY_MS);
      return;
    }
    const queued = queueRef.current;
    queueRef.current = [];
    console.log(`[SPEC-82][TAP] implicit release: welcome over, sending queued=${queued.length} socketOpen=${open}`);
    queued.forEach(({ send }) => {
      try { send(); } catch (e) { console.warn('[SPEC-82][TAP] queued request failed', e); }
    });
  }, []);

  // Owner 2026-10-07: a screen move is not held behind the welcome (it moves the screen at once, silently); it still waits for an
  // open socket (the same L1 rule), because a send into a closed one is dropped without a trace.
  const sendNow = useCallback((send, waitedMs = 0) => {
    const open = openRef.current ? openRef.current() : true;
    if (!open && waitedMs < SOCKET_GIVE_UP_MS) {
      nowTimersRef.current.push(setTimeout(() => sendNow(send, waitedMs + SOCKET_RETRY_MS), SOCKET_RETRY_MS));
      return;
    }
    try { send(); } catch (e) { console.warn('[SPEC-82][TAP] immediate request failed', e); }
  }, []);

  const flush = useCallback(() => deliver(0), [deliver]);
  useEffect(() => () => { clearTimeout(retryRef.current); nowTimersRef.current.forEach(clearTimeout); }, []);

  return useMemo(() => ({ hold, deliver: flush, sendNow }), [hold, flush, sendNow]); // stable: callers list it in effect/callback deps
}
